import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, FilePlus2, Lock } from 'lucide-react';
import { QUOTE_STATUS, QUOTE_TRANSITIONS, dateLabel, type QuoteStatus } from '@adisys/shared';
import { api, ApiRequestError } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { Fact, MetaBadge, Notice, Section, errorText, inr2, qtyLabel, type TabProps } from '../../../lib/crm';
import { Button, Card, Checkbox, EmptyState, ErrorState, Field, Input, Modal, Select, Skeleton, Textarea, useToast } from '../../../components/ui';

type Dialog = null | 'generate' | 'terms' | 'submit' | 'send' | 'revise' | 'approve' | 'reject';

const MODES: Array<[string, string]> = [
  ['purchase_order', 'Customer purchase order'], ['signed_document', 'Signed quotation / contract'],
  ['email', 'Email confirmation'], ['whatsapp', 'WhatsApp confirmation'], ['verbal_confirmed', 'Verbal (authorised person)'],
];

export function QuotationsTab({ project, reload }: TabProps) {
  const { can } = useAuth();
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [kind, setKind] = useState<'quotation' | 'proforma'>('quotation');
  const [form, setForm] = useState<Record<string, any>>({});
  const [channels, setChannels] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [fieldErr, setFieldErr] = useState<ApiRequestError | null>(null);
  const [result, setResult] = useState<any>(null);

  const list = useQuery({ queryKey: ['crm', 'project', project.id, 'quotations'], queryFn: () => api.get(`/crm/projects/${project.id}/quotations`) });
  const rows: any[] = list.data?.data ?? [];
  const detail = useQuery({
    queryKey: ['crm', 'project', project.id, 'quotation', openId],
    queryFn: () => api.get(`/crm/quotations/${openId}`), enabled: Boolean(openId),
  });
  const q = detail.data;

  const done = () => { setDialog(null); setForm({}); setFiles([]); setFieldErr(null); void list.refetch(); void detail.refetch(); reload(); };
  const onErr = (title: string) => (e: unknown) => { setFieldErr(e instanceof ApiRequestError ? e : null); toast.error(title, errorText(e)); };
  const run = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api.post(path, body ?? {}),
    onSuccess: () => done(), onError: onErr('That did not work'),
  });
  const generate = useMutation({
    mutationFn: () => api.post(`/crm/projects/${project.id}/quotations`, { kind, validUntil: form.validUntil || undefined, terms: form.terms || undefined,
      paymentTerms: form.paymentTerms || undefined, deliveryTerms: form.deliveryTerms || undefined, notes: form.notes || undefined }),
    onSuccess: (r: any) => { done(); setOpenId(r.id); toast.success(kind === 'proforma' ? 'Proforma invoice generated' : 'Quotation generated'); },
    onError: onErr('Could not generate the document'),
  });
  const saveTerms = useMutation({
    mutationFn: () => api.patch(`/crm/quotations/${openId}`, { validUntil: form.validUntil || undefined, subject: form.subject || undefined, terms: form.terms || undefined,
      paymentTerms: form.paymentTerms || undefined, deliveryTerms: form.deliveryTerms || undefined, notes: form.notes || undefined }),
    onSuccess: () => done(), onError: onErr('Could not save the terms'),
  });
  const approve = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      for (const k of ['mode', 'approvedByName', 'poNumber', 'poDate', 'note']) if (form[k]) fd.append(k, form[k]);
      files.forEach((f) => fd.append('documents', f));
      return api.upload(`/crm/quotations/${openId}/approve`, fd);
    },
    onSuccess: (r: any) => { setResult(r); done(); },
    onError: onErr('Could not record the approval'),
  });

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const fe = (k: string) => fieldErr?.fieldError(k);
  const open = (d: Dialog, init: Record<string, any> = {}) => { setForm(init); setFiles([]); setFieldErr(null); setChannels([]); setDialog(d); };

  const stageOk = ['quotation', 'approval'].includes(project.stage);
  const active = project.status === 'active';
  const pricingApproved = project.summary?.pricing?.status === 'approved';
  const canGenerate = can('crm.quote.manage') && active && pricingApproved;

  const approveError = (() => {
    if (!form.approvedByName?.trim()) return 'Enter who at the customer approved it.';
    if (form.mode === 'purchase_order' && (!form.poNumber?.trim() || !files.length)) return 'A purchase order approval needs the PO number and the PO document.';
    if (form.mode === 'signed_document' && !files.length) return 'Attach the signed document as evidence.';
    if (!['purchase_order', 'signed_document'].includes(form.mode) && (form.note ?? '').trim().length < 10) return 'Describe who confirmed, when and how (at least a sentence).';
    return null;
  })();

  const next = q ? QUOTE_TRANSITIONS[q.status as QuoteStatus] : [];
  const live = q && !['approved', 'rejected', 'superseded'].includes(q.status);
  const manage = can('crm.quote.manage') && active;
  const decide = can('crm.approval.record') && active && stageOk;

  if (list.isLoading) return <Skeleton className="h-40 w-full" />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => list.refetch()} />;

  return (
    <div className="space-y-5">
      {result && (
        <Notice tone="success">
          Customer approval recorded. Order <strong>{result.orderNo}</strong> was created: {result.reserved} unit(s) reserved from stock,{' '}
          {result.requested} purchase request line(s) raised. The project has moved to procurement.
        </Notice>
      )}
      {!pricingApproved && rows.length === 0 && <Notice>A quotation can be generated once pricing is approved and released to Sales.</Notice>}

      <Section title="Quotations & proforma invoices" description="Generated from the approved pricing; customer-facing figures only."
        action={canGenerate && (
          <div className="flex gap-2">
            <Button size="sm" icon={<FilePlus2 className="h-4 w-4" />} onClick={() => { setKind('quotation'); open('generate'); }}>Generate quotation</Button>
            <Button size="sm" onClick={() => { setKind('proforma'); open('generate'); }}>Generate proforma invoice</Button>
          </div>)}>
        {rows.length === 0 ? <Card><EmptyState title="No documents yet" /></Card> : (
          <Card padded={false} className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead><tr className="border-b border-line bg-sunken/60 text-ink-600">
                {['Number', 'Type', 'Version', 'Status', 'Valid until', 'Total'].map((h, i) => <th key={h} className={`px-3 py-2.5 text-sm font-bold ${i === 5 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-line">
                {rows.map((r) => (
                  <tr key={r.id} className={`cursor-pointer hover:bg-ink-50 ${r.id === openId ? 'bg-brand-50/60' : ''}`} onClick={() => setOpenId(r.id)}>
                    <td className="px-3 py-2.5 font-medium text-ink-900">{r.quoteNo} {r.locked && <Lock className="ml-1 inline h-3.5 w-3.5 text-ink-400" aria-label="Locked" />}</td>
                    <td className="px-3 py-2.5">{r.kind === 'proforma' ? 'Proforma invoice' : 'Quotation'}</td>
                    <td className="px-3 py-2.5">v{r.version}</td>
                    <td className="px-3 py-2.5"><MetaBadge meta={QUOTE_STATUS} value={r.status} /></td>
                    <td className="px-3 py-2.5">{dateLabel(r.validUntil)}</td>
                    <td className="tabular px-3 py-2.5 text-right font-medium">{inr2(r.grandTotal)}</td>
                  </tr>))}
              </tbody>
            </table>
          </Card>
        )}
      </Section>

      {openId && (detail.isLoading ? <Skeleton className="h-48 w-full" /> : detail.error ? <ErrorState error={detail.error} onRetry={() => detail.refetch()} /> : q && (
        <Card className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-ink-900">{q.quoteNo}</h3>
              <p className="text-xs text-ink-500">{q.subject}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <MetaBadge meta={QUOTE_STATUS} value={q.status} />
              <Button size="sm" icon={<ExternalLink className="h-4 w-4" />} onClick={() => window.open(`/crm/quotations/${q.id}/document`, '_blank', 'noopener')}>View / print / PDF</Button>
            </div>
          </div>

          {q.locked && <Notice tone="success">Approved by the customer and locked. It can no longer be edited or revised.</Notice>}
          {q.order && (
            <div className="rounded-lg bg-success-soft p-3.5">
              <p className="text-sm font-semibold text-success">Order confirmation {q.order.orderNo}</p>
              <dl className="mt-2 grid gap-3 sm:grid-cols-4">
                <Fact label="Customer PO">{q.order.customerPoNo}</Fact>
                <Fact label="Approved by">{q.order.approvedByName}</Fact>
                <Fact label="Mode">{String(q.order.approvalMode).replace(/_/g, ' ')}</Fact>
                <Fact label="Recorded">{dateLabel(q.order.createdAt)}</Fact>
              </dl>
            </div>)}

          <div className="overflow-x-auto rounded-lg ring-1 ring-line">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead><tr className="border-b border-line bg-sunken/60 text-ink-600">
                {['#', 'Description', 'Qty', 'Rate', 'Taxable', 'GST', 'Amount'].map((h, i) => <th key={h} className={`px-3 py-2 text-xs font-bold ${i > 1 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-line">
                {(q.lines as any[]).map((l) => (
                  <tr key={l.lineNo}><td className="px-3 py-2 text-ink-500">{l.lineNo}</td>
                    <td className="px-3 py-2"><p className="font-medium text-ink-900">{l.description}</p><p className="text-xs text-ink-500">{[l.brandName, l.modelNumber].filter(Boolean).join(' · ')}</p></td>
                    <td className="tabular px-3 py-2 text-right">{qtyLabel(l.quantity)} {l.unit}</td>
                    <td className="tabular px-3 py-2 text-right">{inr2(l.unitPrice)}</td>
                    <td className="tabular px-3 py-2 text-right">{inr2(l.taxable)}</td>
                    <td className="tabular px-3 py-2 text-right">{inr2(l.gst)} <span className="text-xs text-ink-500">({l.gstRate}%)</span></td>
                    <td className="tabular px-3 py-2 text-right font-medium">{inr2(l.total)}</td></tr>))}
              </tbody>
            </table>
          </div>
          <dl className="ml-auto w-full max-w-xs space-y-1 text-sm">
            {[['Goods subtotal', q.goodsSubtotal], ['Discount', -q.discountAmount], ['Taxable value', q.taxableTotal], ['GST', q.gstTotal]].map(([k, v]) => (
              <div key={k as string} className="flex justify-between"><dt className="text-ink-600">{k}</dt><dd className="tabular">{inr2(v as number)}</dd></div>))}
            <div className="flex justify-between border-t border-line pt-2 text-base font-semibold"><dt>Grand total</dt><dd className="tabular">{inr2(q.grandTotal)}</dd></div>
          </dl>
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <Fact label="Payment terms">{q.paymentTerms}</Fact><Fact label="Delivery terms">{q.deliveryTerms}</Fact>
            <Fact label="Terms and conditions"><span className="whitespace-pre-wrap">{q.terms}</span></Fact><Fact label="Notes">{q.notes}</Fact>
          </dl>

          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            {manage && q.status === 'draft' && (<>
              <Button onClick={() => open('terms', { validUntil: q.validUntil ?? '', subject: q.subject ?? '', terms: q.terms ?? '', paymentTerms: q.paymentTerms ?? '', deliveryTerms: q.deliveryTerms ?? '', notes: q.notes ?? '' })}>Edit terms</Button>
              <Button variant="primary" disabled={!stageOk} onClick={() => open('submit')}>Submit to customer</Button>
            </>)}
            {manage && !['draft', 'superseded'].includes(q.status) && <Button onClick={() => open('send')}>Send again</Button>}
            {manage && next.includes('viewed') && <Button onClick={() => run.mutate({ path: `/crm/quotations/${q.id}/status`, body: { status: 'viewed' } })}>Mark viewed</Button>}
            {manage && next.includes('negotiation') && <Button onClick={() => open('negotiation' as any, { note: '' })}>Mark in negotiation</Button>}
            {manage && ['submitted', 'viewed', 'negotiation', 'rejected'].includes(q.status) && stageOk && !q.locked && (
              <Button onClick={() => open('revise', { reason: '' })}>Revise</Button>)}
            {decide && live && q.status !== 'draft' && (<>
              <Button variant="success" onClick={() => open('approve', { mode: 'purchase_order' })}>Record customer approval</Button>
              <Button variant="danger" onClick={() => open('reject', { reason: '' })}>Record rejection</Button>
            </>)}
          </div>
          {!stageOk && live && <Notice>This project is no longer at the quotation or approval stage, so the document can be viewed but not progressed.</Notice>}
        </Card>
      ))}

      <Modal open={dialog === 'generate'} onClose={() => setDialog(null)} size="lg"
        title={kind === 'proforma' ? 'Generate proforma invoice' : 'Generate quotation'} description="Lines and prices come from the approved pricing; terms can be edited now or while the document is a draft."
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" loading={generate.isPending} onClick={() => generate.mutate()}>Generate</Button></>}>
        <Terms form={form} set={set} fe={fe} />
      </Modal>
      <Modal open={dialog === 'terms'} onClose={() => setDialog(null)} size="lg" title="Edit terms"
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" loading={saveTerms.isPending} onClick={() => saveTerms.mutate()}>Save</Button></>}>
        <Terms form={form} set={set} fe={fe} />
      </Modal>
      <Modal open={dialog === 'submit'} onClose={() => setDialog(null)} title="Submit to the customer"
        description="The document becomes read-only. Changes after this go through a revision."
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" loading={run.isPending}
          onClick={() => run.mutate({ path: `/crm/quotations/${q?.id}/submit`, body: channels.length ? { send: { channels } } : {} })}>Submit</Button></>}>
        <Channels channels={channels} setChannels={setChannels} />
      </Modal>
      <Modal open={dialog === 'send'} onClose={() => setDialog(null)} title="Send to the customer"
        description="Messages are queued for delivery to the customer's email or WhatsApp number on file."
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" disabled={!channels.length} loading={run.isPending}
          onClick={() => run.mutate({ path: `/crm/quotations/${q?.id}/send`, body: { channels } })}>Queue message</Button></>}>
        <Channels channels={channels} setChannels={setChannels} />
      </Modal>
      <Modal open={dialog === ('negotiation' as any)} onClose={() => setDialog(null)} title="Mark as in negotiation"
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" loading={run.isPending}
          onClick={() => run.mutate({ path: `/crm/quotations/${q?.id}/status`, body: { status: 'negotiation', note: form.note || undefined } })}>Save</Button></>}>
        <Field label="What is being negotiated?"><Textarea rows={3} value={form.note ?? ''} onChange={(e) => set('note', e.target.value)} /></Field>
      </Modal>
      <Modal open={dialog === 'revise'} onClose={() => setDialog(null)} size="lg" title="Create a revision"
        description="A new version is created and this one is superseded."
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" disabled={(form.reason ?? '').trim().length < 5} loading={run.isPending}
          onClick={() => run.mutate({ path: `/crm/quotations/${q?.id}/revise`, body: { reason: form.reason, validUntil: form.validUntil || undefined, paymentTerms: form.paymentTerms || undefined, deliveryTerms: form.deliveryTerms || undefined, terms: form.terms || undefined } })}>Create revision</Button></>}>
        <div className="space-y-3">
          <Field label="Reason for the revision" required error={fe('reason')}><Textarea rows={2} value={form.reason ?? ''} onChange={(e) => set('reason', e.target.value)} /></Field>
          <Terms form={form} set={set} fe={fe} optional />
        </div>
      </Modal>
      <Modal open={dialog === 'reject'} onClose={() => setDialog(null)} title="Record customer rejection"
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="danger" disabled={(form.reason ?? '').trim().length < 5} loading={run.isPending}
          onClick={() => run.mutate({ path: `/crm/quotations/${q?.id}/reject`, body: { reason: form.reason } })}>Record rejection</Button></>}>
        <Field label="Why did the customer decline?" required><Textarea rows={3} value={form.reason ?? ''} onChange={(e) => set('reason', e.target.value)} /></Field>
      </Modal>
      <Modal open={dialog === 'approve'} onClose={() => setDialog(null)} size="lg" title="Record customer approval"
        description="Approving locks this document and creates the order. Procurement and Finance are notified."
        footer={<><Button onClick={() => setDialog(null)}>Cancel</Button><Button variant="success" disabled={Boolean(approveError)} loading={approve.isPending} onClick={() => approve.mutate()}>Approve and create order</Button></>}>
        <div className="space-y-3">
          <Field label="How did the customer confirm?" required error={fe('mode')}>
            <Select value={form.mode ?? 'purchase_order'} onChange={(e) => set('mode', e.target.value)}>{MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Approved by (customer)" required error={fe('approvedByName')}><Input value={form.approvedByName ?? ''} onChange={(e) => set('approvedByName', e.target.value)} /></Field>
            {form.mode === 'purchase_order' && <>
              <Field label="Customer PO number" required error={fe('poNumber')}><Input value={form.poNumber ?? ''} onChange={(e) => set('poNumber', e.target.value)} /></Field>
              <Field label="PO date" error={fe('poDate')}><Input type="date" value={form.poDate ?? ''} onChange={(e) => set('poDate', e.target.value)} /></Field></>}
          </div>
          <Field label="Note" required={!['purchase_order', 'signed_document'].includes(form.mode)} error={fe('note')}
            hint="Who confirmed, when and how.">
            <Textarea rows={3} value={form.note ?? ''} onChange={(e) => set('note', e.target.value)} /></Field>
          <Field label="Evidence (PO, signed copy, screenshot)" required={['purchase_order', 'signed_document'].includes(form.mode)} error={fe('documents')} hint="JPG, PNG, WEBP or PDF, up to 5 files.">
            <input type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="text-sm"
              onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 5))} /></Field>
          {approveError && <Notice tone="warning">{approveError}</Notice>}
        </div>
      </Modal>
    </div>
  );
}

function Terms({ form, set, fe, optional }: { form: Record<string, any>; set: (k: string, v: any) => void; fe: (k: string) => string | undefined; optional?: boolean }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Valid until" error={fe('validUntil')} hint={optional ? undefined : 'Defaults to 30 days'}><Input type="date" value={form.validUntil ?? ''} onChange={(e) => set('validUntil', e.target.value)} /></Field>
        <Field label="Payment terms" error={fe('paymentTerms')}><Input value={form.paymentTerms ?? ''} placeholder={optional ? 'Keep current' : 'Default terms apply'} onChange={(e) => set('paymentTerms', e.target.value)} /></Field>
      </div>
      <Field label="Delivery terms" error={fe('deliveryTerms')}><Input value={form.deliveryTerms ?? ''} placeholder={optional ? 'Keep current' : 'Default terms apply'} onChange={(e) => set('deliveryTerms', e.target.value)} /></Field>
      <Field label="Terms and conditions" error={fe('terms')}><Textarea rows={4} value={form.terms ?? ''} placeholder={optional ? 'Keep current' : 'Default terms apply'} onChange={(e) => set('terms', e.target.value)} /></Field>
      <Field label="Notes" error={fe('notes')}><Textarea rows={2} value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)} /></Field>
    </div>
  );
}

function Channels({ channels, setChannels }: { channels: string[]; setChannels: (c: string[]) => void }) {
  const toggle = (c: string, on: boolean) => setChannels(on ? [...channels, c] : channels.filter((x) => x !== c));
  return (
    <div className="space-y-2">
      <p className="text-sm text-ink-700">Also send to the customer:</p>
      <Checkbox label="Email" checked={channels.includes('email')} onChange={(e) => toggle('email', e.target.checked)} />
      <Checkbox label="WhatsApp" checked={channels.includes('whatsapp')} onChange={(e) => toggle('whatsapp', e.target.checked)} />
    </div>
  );
}
